import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LogementService } from '../../services/logement.service';
import { Logement } from '../../models/logement';
import { combineLatest, Observable, Subscription } from "rxjs";
import { ActualitesService } from "../../services/actualites.service";
import { Actualite } from "../../models/actualite";
import { EquipementService } from "../../services/equipement.service";
import { Equipement } from "../../models/equipement";
import { CarnetEquipement } from "../../models/carnetEquipement";
import { DocumentConstructeurService } from "../../services/document-constructeur.service";
import { Document } from "../../models/document/document";
import {filter, map} from "rxjs/operators";
import { faEye } from '@fortawesome/free-solid-svg-icons';
import {UserService} from "../../services/user.service";
import {Conseil} from "../../models/conseil";
import {ConseilService} from "../../services/conseil.service";
import { gridToBreakpoints } from '../../swiper.config';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {

  // Grille reprise à l'identique de l'ancienne configuration ngu-carousel :
  // 1,2 carte sur mobile pour laisser entrevoir la suivante, jusqu'à 3 en large.
  carouselBreakpoints = gridToBreakpoints({ xs: 1.2, sm: 2.2, md: 2, lg: 3 });

  faEye = faEye;

  logement$: Observable<Logement> = null;
  conseilsAndActualites: any[] = [];
  carnetEquipements$: Promise<CarnetEquipement[]> = null;
  documents$: Observable<Document[]> = null;

  private subscription: Subscription;

  constructor(
    private logementService: LogementService,
    private actualiteService: ActualitesService,
    private equipementService: EquipementService,
    private documentService: DocumentConstructeurService,
    private userService: UserService,
    private conseilService: ConseilService,
  ) {
  }

  ngOnInit(): void {
    this.logement$ = this.logementService.logementCourant;
    this.subscription = this.logement$.subscribe(logement => {
      this.getConseilsAndActualites(logement);
      this.carnetEquipements$ = this.equipementService.load(logement.id);
      const documentsConseilles$ = this.documentService.loadConseilles(logement.id);
      const documentsAutres$ = this.documentService.loadAutres(logement.id);
      this.documents$ = combineLatest(documentsConseilles$, documentsAutres$).pipe(map(([conseilles, autres]) => {
        return conseilles.concat(autres);
      }));

      this.documents$ = this.documents$.pipe(map(documents => {
        return documents.filter(document => {
          return (this.userService.isProprietaire(logement) && document.roles.includes('ROLE_COPROPRIETAIRE')) ||
          (this.userService.isLocataire(logement) && document.roles.includes('ROLE_LOCATAIRE'));
        });
      }));
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get displayCorrespondantClient(): Observable<boolean> {
    return this.logement$.pipe(map(logement => logement.correspondantClient));
  }

  private getConseilsAndActualites(logement: Logement){
    const promiseActualites = new Promise<Actualite[]>((resolve, reject) => {
      this.actualiteService.load(logement.id).subscribe((actualites) => {
        resolve(actualites);
      });
    });
    const promiseConseils = new Promise<Conseil[]>((resolve, reject) => {
      this.conseilService.load().subscribe((conseils) => {
        resolve(conseils);
      });
    });
    Promise.all([promiseConseils, promiseActualites]).then((values: any) => {
      this.conseilsAndActualites = values[0].concat(values[1]);

      this.conseilsAndActualites.sort(((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        if (dateA.getTime() < dateB.getTime()){
          return 1;
        }

        if (dateA.getTime() > dateB.getTime()){
          return -1;
        }

        return 0;
      }));
    });
  }

  getCoverSubtitle(logement: Logement) {
    let adresse;

    if (logement.appartement && logement.batiment) {
      adresse = logement.batiment.residence.adresse;
    } else {
      adresse = logement.adresse;
    }

    if (logement.appartement && logement.batiment) {
      if (adresse) {
        return `${logement.batiment.residence.nom} - ${adresse.toString()}`;
      } else {
        return `${logement.batiment.residence.nom}`;
      }
    } else {
      if (adresse) {
        return `${adresse.toString()}`;
      }
    }

    return '';
  }

  isActualite(object){
    if (object instanceof Actualite){
      return object;
    }
    return null;
  }

  isConseil(object){
    if (object instanceof Conseil){
      return object;
    }
    return null;
  }
}
